/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import useHttp from "hooks/http.hook";
import { FC, useEffect, useState } from "react";
import { ProviderInterface, QueryResultInterface } from "@malmur-technologies/medicalsystem-shared";
import { useNavigate } from "react-router-dom";
import { CoreAPI_URLs } from "constants/http.const";
import Box from "components/generals/Box";
import Button from "components/generals/Button";
import { useTranslation } from "react-i18next";
import { RoutesConstants } from "@malmur-technologies/medicalsystem-shared";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { EntityPermissions, PermissionsUser } from "types";
import NurseTable from "components/tables/NurseTable";
import useSort from "hooks/useSort.hook";

/**
 * Component displaying paginated list of nurses with search and navigation.
 * Provides table view of nurse providers with options to create new nurse records.
 * 
 * @returns Nurse data content with table, pagination, and create button
 */
const NursesDataContent: FC = () => {
  const [isLoading, _error, sendRequest] = useHttp();
  const [nurseListResponse, setNurseListResponse] = useState<ProviderInterface[]>([]);
  const [nurseFilter, setNurseFilter] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [pageTotal, setPageTotal] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowsTotal, setRowsTotal] = useState<number>(0);
  const { sortBy, sortOrder, handleSortChange, buildSortParam } = useSort();
  const navigate = useNavigate();

  const { t } = useTranslation();

  function handleNewNurse() {
    navigate(RoutesConstants.NEW_NURSE);
  }

  useEffect(() => {
    const nameParam = nurseFilter ? `name=${nurseFilter}&` : "";
    const url = `${CoreAPI_URLs.NURSE_GET_ALL}?${nameParam}${buildSortParam()}page=${page}&pageSize=${pageSize}`;

    sendRequest(url, onGetNurseHandler, "GET");
  }, [nurseFilter, page, pageSize, buildSortParam]);

  const onGetNurseHandler = (nursesResponse: QueryResultInterface<ProviderInterface>) => {
    setNurseListResponse(nursesResponse.results);
    setRowsTotal(nursesResponse.total);
    setPageTotal(Math.ceil(nursesResponse.total / pageSize));
  };

  return (
    <Box>
      <Box mt={2} mb={2} width="100%" display="flex" justifyContent="flex-end">
        <PermissionWrapper
          requiredPermissions={[PermissionsUser[EntityPermissions.NURSE].CREATE]}
        >
          <Button id="cy-new-nurse-button" type="button" variant="contained" color="info" onClick={handleNewNurse}>
            {t("nurseDataContent.newNurse")}
          </Button>
        </PermissionWrapper>
      </Box>
      <NurseTable
        modelList={nurseListResponse}
        setModelList={setNurseListResponse}
        canSearchPlaceholder={t("nurseDataContent.searchNurse")}
        setModelFilter={setNurseFilter}
        entriesPerPage={{ defaultValue: pageSize }}
        setPageState={setPage}
        setPageSizeState={setPageSize}
        apiPagination={true}
        apiPageTotal={pageTotal}
        apiRowsTotal={rowsTotal}
        apiPageCurrentIndex={page}
        isLoading={isLoading}
        onSortChange={handleSortChange}
        currentSortBy={sortBy}
        currentSortOrder={sortOrder}
      />
    </Box>
  );
};

export default NursesDataContent;
